import { AfterViewInit, Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { CommonService } from 'src/app/services/common/common.service';
import { ContactService } from 'src/app/services/register-data/contacts/contact.service';
import { ValidatorService } from 'src/app/services/validators/errors-validators.service';
import { listaFlagSimNao, listaGeneros } from 'src/models/const';
import { Contact, ContatoResponsavel } from 'src/models/interfaces/contact-interface';
import { TipoMeioContato } from 'src/models/interfaces/contacts/tipo-meio-contato-interface';
import { DATE_FORMAT_YEARLESS } from 'src/models/interfaces/date-format.interface';
import { Department } from 'src/models/interfaces/departments/department.interface';
import { MeioContato } from 'src/models/interfaces/empresa/empresa.interface';
import { TypeAccess } from 'src/models/interfaces/input-interface';
import { Role } from 'src/models/interfaces/role.interface';
import { filter } from 'rxjs/operators';
import { Regex } from 'src/models/interfaces/regex/regex-enum';

@Component({
  selector: 'spe-novo-contato',
  templateUrl: './novo-contato.component.html',
  styleUrls: ['./novo-contato.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT_YEARLESS }
  ],
  standalone: false
})
export class NovoContatoComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() isEditMode = false;
  desabilitarApagar = false;
  telefonesRemovidos: number[] = [];
  telefonesOriginais: MeioContato[] = [];
  listDepartment: Department[] = [];
  listRoles: Role[] = [];
  listFlagResponsible: TypeAccess[] = listaFlagSimNao;
  listGender: TypeAccess[] = listaGeneros;
  tiposDeContato: TipoMeioContato[] = [];

  public meioContatoPrincipal?: MeioContato;
  public meioContatoSecundario?: MeioContato;

  validatorErrors = this.validatorService.validatorErrors;

  private subscriptions: Subscription[] = [];

  public isNotChanged = true;

  private initialValues!: {
    nomeContato: string;
    flagResponsible: string;
    secundaryEmails: [{ email: string; isPrimary: boolean }, { email: string; isPrimary: boolean }];
  };

  public phoneHasNotChanged = true;

  private initialPhoneValues!: { nomeContato: string; flagResponsible: string };
  // sombra no header do dialog
  hasScrolled = false;
  @ViewChild('dialogScrollArea', { static: true })
  dialogScrollAreaRef!: ElementRef<HTMLDivElement>;

  public contactDataForm = new FormGroup({
    nomeContato: new FormControl('', [Validators.required]),
    flagResponsible: new FormControl('N', [Validators.required]),
    secundaryEmails: new FormArray([
      new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        isPrimary: new FormControl(false)
      }),
      new FormGroup({
        email: new FormControl('', [Validators.email]),
        isPrimary: new FormControl(false)
      })
    ]),
    additionalPhones: new FormArray([
      // new FormGroup({
      //   id: new FormControl<number | null>(null),
      //   tipoContato: new FormControl<number | null>(null, Validators.required),
      //   phoneNumber: new FormControl('', Validators.required),
      //   ramal: new FormControl('')
      // })
    ]),
    idContato: new FormControl<null | number>(null)
  });

  constructor(
    private fb: FormBuilder,
    private contactService: ContactService,
    private dialogRef: MatDialogRef<NovoContatoComponent>,
    public readonly validatorService: ValidatorService,
    public readonly commonService: CommonService,
    @Inject(MAT_DIALOG_DATA) public data?: { contact?: ContatoResponsavel; desabilitarApagar?: boolean }
  ) {
    if (data) {
      this.isEditMode = true;
      this.desabilitarApagar = data.desabilitarApagar ?? false;
    }
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    this.dialogScrollAreaRef.nativeElement.addEventListener('scroll', () => {
      this.hasScrolled = this.dialogScrollAreaRef.nativeElement.scrollTop > 0;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
  getPhoneMask(tipoContatoId: number): string {
    if (tipoContatoId === 4) {
      return '(00) 00000-0000'; // 11 digits
    } else if (tipoContatoId === 1 || tipoContatoId === 2) {
      return '(00) 0000-0000'; // 10 digits
    }
    return '(00) 00000-0000'; // default fallback
  }
  private loadData() {
    if (this.isEditMode) {
      const contactData = this.data?.contact;

      const subscriptionMeiosContato = this.contactService.getMeiosContato(contactData!.idContato).subscribe({
        next: (contato) => {
          this.additionalPhones.clear();
          this.secundaryEmails.clear();
          let hasContactNumbers = false;

          this.meioContatoSecundario = contato.find(
            (meioContato: MeioContato) => meioContato.tipo === 6 && meioContato.flagPrincipal === 'N'
          );
          contato.forEach((contactItem: MeioContato | {}) => {
            const isValid = 'tipo' in contactItem;

            if (isValid && contactItem.tipo !== 6) {
              this.adicionarOutroTelefone(contactItem);
              this.telefonesOriginais.push(contactItem);
              hasContactNumbers = true;
            }

            if (isValid && contactItem.tipo === 6) {
              this.adicionarEmail(contactItem);
            }
          });
          this.initialValues = {
            nomeContato: this.contactDataForm.get('nomeContato')!.value!,
            flagResponsible: this.contactDataForm.get('flagResponsible')!.value!,
            secundaryEmails: [
              ...(this.secundaryEmails.controls.map((email) => ({
                email: email!.get('email')?.value || '',
                isPrimary: email!.get('isPrimary')?.value || false
              })) as [{ email: string; isPrimary: boolean }, { email: string; isPrimary: boolean }])
            ]
          };

          // Listen for changes in those 2 fields
          this.contactDataForm.valueChanges.subscribe((values) => {
            const nomeIgual = values.nomeContato === this.initialValues.nomeContato;
            const flagIgual = values.flagResponsible === this.initialValues.flagResponsible;
            const emailsIguais = values.secundaryEmails!.every((secEmail, idx) => {
              return (
                secEmail.email === (this.initialValues.secundaryEmails?.[idx]?.email || '') &&
                secEmail.isPrimary === (this.initialValues.secundaryEmails?.[idx]?.isPrimary || false)
              );
            });

            this.isNotChanged = nomeIgual && flagIgual && emailsIguais;
          });
          this.meioContatoPrincipal = contato.find(
            (meioContato: MeioContato) => meioContato.tipo === 6 && meioContato.flagPrincipal === 'S'
          );

          if (this.secundaryEmails.length < 2) {
            this.adicionarEmail();
          }
          if (!hasContactNumbers) {
            this.adicionarOutroTelefone();
          }
        }
      });

      this.contactDataForm.patchValue({
        nomeContato: contactData?.nomeContato,
        flagResponsible: contactData?.flagResponsavel,
        idContato: contactData?.idContato!
      });
      this.subscriptions.push(subscriptionMeiosContato);
    } else {
      this.adicionarOutroTelefone();
    }

    this.subscriptions.push(
      this.contactService.getContactsDepartments().subscribe((data) => {
        this.listDepartment = data;
      }),

      this.contactService.getContactsRoles().subscribe((data) => {
        this.listRoles = data;
      }),

      this.contactService.getMeiosContatoTipo().subscribe({
        next: (tipos) => {
          this.tiposDeContato = tipos;
        }
      })
    );
  }

  onSave(): void {
    const request = {
      nomeContato: this.contactDataForm.controls.nomeContato.value,
      flagResponsavelCompra: this.contactDataForm.controls.flagResponsible.value,
      idCargo: null,
      idDepartamento: null,
      sexo: null,
      aniversario: null,
      meiosContato: [
        ...this.secundaryEmails.controls
          .filter((group) => group.get('email')?.value !== '')
          .map((group) => {
            return {
              _complemento: '',
              descricao: group.get('email')?.value,
              idContato: this.data?.contact?.idContato,
              tipo: 6,
              id: group.get('isPrimary')?.value ? this.meioContatoPrincipal?.id : this.meioContatoSecundario?.id
            };
          })
      ],
      idContato: this.contactDataForm.controls.idContato.value
    };
    this.dialogRef.close({
      emailToDelete: request.meiosContato.length === 1 ? this.meioContatoSecundario?.id : null,
      telefones: this.additionalPhones,
      telefonesOriginais: this.telefonesOriginais,
      telefonesRemovidos: this.telefonesRemovidos,
      idContato: this.data?.contact?.idContato,
      isNotChanged: this.isNotChanged,
      data: request,
      action: 'save'
    });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onDelete(): void {
    this.dialogRef.close({ action: 'delete' });
  }

  onFlagResponsibleChange(event: MatCheckboxChange): void {
    this.contactDataForm.get('flagResponsible')?.setValue(event.checked ? 'S' : 'N');
  }

  get contactDataFormControl() {
    return this.contactDataForm.controls;
  }

  // FormArray getters
  get additionalPhones(): FormArray {
    return this.contactDataForm.get('additionalPhones') as FormArray;
  }

  get secundaryEmails(): FormArray {
    return this.contactDataForm.get('secundaryEmails') as FormArray;
  }

  // FormArray controls
  get additionalPhonesControls() {
    return this.additionalPhones.controls as FormGroup[];
  }

  get secundaryEmailsControls() {
    return this.secundaryEmails.controls as FormGroup[];
  }

  adicionarOutroTelefone(contactItem?: MeioContato): void {
    const group = new FormGroup({
      id: new FormControl(contactItem?.id ?? null),
      tipoContato: new FormControl(contactItem?.tipo ?? '', Validators.required),
      phoneNumber: new FormControl(contactItem?.descricao?.match(/\d+/)?.[0] ?? '', Validators.required),
      ramal: new FormControl(contactItem?.complemento ?? '')
    });

    // Subscribe to tipoContato value changes
    group.get('tipoContato')?.valueChanges.subscribe((tipo) => {
      const phoneCtrl = group.get('phoneNumber')!;
      // Reset validators every time before applying new ones
      phoneCtrl.clearValidators();

      if (tipo === 4) {
        // Enforce (NN) 9NNNN-NNNN format
        phoneCtrl.setValidators([Validators.required, Validators.pattern(Regex.Celular)]);
      } else {
        // Standard (NN) *0-8*NNN-NNNN format
        phoneCtrl.setValidators([Validators.required, Validators.pattern(Regex.Fixo)]);
      }
      phoneCtrl.updateValueAndValidity({ emitEvent: false });
    });

    this.additionalPhones.push(group);
  }

  removerTelefone(index: number): void {
    const tel = this.additionalPhones.at(index)?.value;

    console.log(tel);

    if (tel?.id) {
      this.telefonesRemovidos.push(tel?.id);
    }
    console.log(this.telefonesRemovidos);
    this.additionalPhones.removeAt(index);
  }

  adicionarEmail(contactItem?: MeioContato) {
    const validators =
      contactItem?.flagPrincipal === 'S' ? [Validators.required, Validators.email] : [Validators.email];

    this.secundaryEmails.push(
      new FormGroup({
        email: new FormControl(contactItem?.descricao || '', validators),
        isPrimary: new FormControl(contactItem?.flagPrincipal === 'S')
      })
    );
  }

  removerEmail(index: number): void {
    this.secundaryEmails.removeAt(index);
  }

  hasPrimaryEmail(): boolean {
    return this.secundaryEmails.controls.some((group) => group.get('isPrimary')?.value);
  }

  onEmailPrimaryChange(index: number): void {
    this.secundaryEmails.controls.forEach((group, i) => {
      if (i !== index) {
        group.get('isPrimary')?.setValue(false, { emitEvent: false });
        group.get('email')?.removeValidators(Validators.required);
      } else {
        group.get('isPrimary')?.setValue(true, { emitEvent: false });
        group.get('email')?.addValidators(Validators.required);
      }
    });
  }
}
